import React, { useState } from 'react';
import type { ServerConfig } from '../types';

interface OutputTableProps {
  configurations: ServerConfig[];
  loading: boolean;
  error: string | null;
  toggleSelections: { [groupID: string]: string };
  setToggleSelections: React.Dispatch<React.SetStateAction<{ [groupID: string]: string }>>;
}

const OutputTable: React.FC<OutputTableProps> = ({ configurations = [], loading, error, toggleSelections, setToggleSelections }) => {
  // Custom sort logic
  const getPriority = (role: string) => {
    if (role === 'VS Server' || role === 'All-in-One Server') return 0;
    if (role.includes('Processing') || role.includes('OX6 Processing')) return 1;
    if (role.includes('Database')) return 2;
    if (role === 'Research Server') return 3;
    return 4;
  };

  // Feature flag: hide incomplete systems (any config with 'Missing Match' SKU)
  const hideIncompleteSystems = true; // Set to false to disable filtering

  // Check for any missing match in all configurations
  const hasAnyMissingMatch = hideIncompleteSystems && configurations.some(cfg => cfg.sku === 'Missing Match');
  if (hasAnyMissingMatch) {
    console.warn('System excluded due to missing match', configurations);
  }

  // --- Grouping logic for toggle-eligible pairs ---
  // Group by toggleGroupID (if present), otherwise by index
  const grouped: { [key: string]: ServerConfig[] } = {};
  configurations.forEach((config, idx) => {
    const groupKey = config.toggleGroupID && config.toggleGroupID !== '' ? config.toggleGroupID : `__${idx}`;
    if (!grouped[groupKey]) grouped[groupKey] = [];
    grouped[groupKey].push(config);
  });
  let groupKeys = Object.keys(grouped);

  // Helper: get default form factor for a group (prefer Rack Mount)
  const getDefaultFormFactor = (group: ServerConfig[]) => {
    const rack = group.find(cfg => cfg.formFactor?.toLowerCase().includes('rack'));
    if (rack) return rack.formFactor!;
    const tower = group.find(cfg => cfg.formFactor?.toLowerCase().includes('tower'));
    if (tower) return tower.formFactor!;
    return group[0].formFactor!;
  };

  // Helper: render pill toggle (modern pill-style switch)
  const renderPillToggle = (groupID: string, group: ServerConfig[]) => {
    const options = group.map(cfg => cfg.formFactor!).filter(Boolean);
    // Always order: Rack Mount left, Tower right
    const uniqueOptions = Array.from(new Set(options));
    const orderedOptions = [
      uniqueOptions.find(opt => opt.toLowerCase().includes('rack')),
      uniqueOptions.find(opt => opt.toLowerCase().includes('tower'))
    ].filter(Boolean);
    const selected = toggleSelections[groupID] || getDefaultFormFactor(group);
    return (
      <div className="flex gap-2 justify-end mb-2">
        <div className="inline-flex rounded-full border border-gray-300 bg-gray-50">
          {orderedOptions.map(option => (
            option ? (
              <button
                key={option}
                type="button"
                className={`px-3 py-1 text-sm rounded-full focus:outline-none transition-colors`}
                onClick={() => setToggleSelections(sel => ({ ...sel, [groupID]: option }))}
                style={{
                  minWidth: 80,
                  color: selected === option ? '#fff' : '#485961',
                  fontWeight: selected === option ? 600 : 400,
                  backgroundColor: selected === option ? '#0099DA' : 'transparent',
                  borderRadius: 9999,
                  border: 'none',
                  outline: 'none',
                  transition: 'color 0.2s, background-color 0.2s',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
                onMouseOver={e => {
                  if (selected !== option) {
                    (e.currentTarget as HTMLButtonElement).style.color = '#00648F';
                  }
                }}
                onMouseOut={e => {
                  if (selected !== option) {
                    (e.currentTarget as HTMLButtonElement).style.color = '#485961';
                  }
                }}
              >
                {option.toLowerCase().includes('rack') ? 'Rack Mount' : 'Tower'}
              </button>
            ) : null
          ))}
        </div>
      </div>
    );
  };

  // Sort groups by priority of the first config in each group
  const sortedGroupKeys = groupKeys.sort((a, b) => {
    const pa = getPriority(grouped[a][0].serverRole);
    const pb = getPriority(grouped[b][0].serverRole);
    return pa - pb;
  });

  // Find the first toggle-eligible group (if any)
  const firstToggleGroupID = sortedGroupKeys.find(
    groupID => grouped[groupID].length > 1 && grouped[groupID][0].toggleGroupID
  );
  const firstToggleGroup = firstToggleGroupID ? grouped[firstToggleGroupID] : null;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-4 relative">
        {/* Pill toggle above table, top-right */}
        {firstToggleGroup && !hasAnyMissingMatch && (
          <div className="absolute right-6 top-6 z-10" style={{ transform: 'translateY(-5px)' }}>
            {renderPillToggle(firstToggleGroupID!, firstToggleGroup)}
          </div>
        )}
        <h2 className="text-xl font-medium mb-3 text-milestone">Recommended System</h2>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-3">
            {error}
          </div>
        )}
        {/* Show message if any system is incomplete */}
        {hasAnyMissingMatch && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded mb-4 text-center">
            <div className="font-semibold text-lg mb-1">System not available</div>
            <div>One or more components in this configuration are still being finalized. Please try again later or adjust your selection.</div>
          </div>
        )}
        {!hasAnyMissingMatch && (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead style={{ backgroundColor: '#0099DA' }}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider w-1/4">Server Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider w-2/4">SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider w-1/4">Qty</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-500">
                    Calculating...
                  </td>
                </tr>
              ) : sortedGroupKeys.length > 0 ? (
                sortedGroupKeys.map((groupID, idx) => {
                  const group = grouped[groupID];
                  // If toggleGroupID is present and group has both Tower and Rack, render pill toggle
                  if (group.length > 1 && group[0].toggleGroupID) {
                    const selectedFormFactor = toggleSelections[groupID] || getDefaultFormFactor(group);
                    const selectedConfig = group.find(cfg => cfg.formFactor === selectedFormFactor) || group[0];
                    return (
                      <tr key={groupID} className="relative">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                          <div className="flex flex-col">
                            <span>{selectedConfig.serverRole}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                          <span>{selectedConfig.sku}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                          <span>{selectedConfig.qty}</span>
                        </td>
                      </tr>
                    );
                  } else {
                    // Render as normal row
                    const config = group[0];
                    return (
                      <tr key={groupID}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{config.serverRole}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{config.sku}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{config.qty}</td>
                  </tr>
                    );
                  }
                })
              ) : (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-500">
                    No configurations to display
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      </div>
    </div>
  );
};

export default OutputTable;
